import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { LanguageSwitcher } from '../../shared/LanguageSwitcher';
import { useAuth } from '../../shared/auth/AuthContext';
import { CHIP } from '../../shared/ui';
import { AboutSlideshow } from './AboutSlideshow';
import { HeroCollage } from './HeroCollage';
import { HELIOS_LOGO, HERO_IMAGE, SERVICES, SOCIALS } from './content';
import { Reveal } from './motion';
import { PhotoLightbox } from './PhotoLightbox';

/**
 * DESIGN.md §11: a control is a pill, and a button that sits on a colour panel
 * is translucent white glass. 48px tall on this page: these are the first
 * things a thumb reaches for.
 */
const PILL =
  'inline-flex items-center justify-center min-h-[48px] px-6 rounded-full text-body-md font-medium whitespace-nowrap transition-[background-color,transform] duration-pop ease-out-strong active:scale-[0.98]';
const PILL_INK = `${PILL} bg-primary text-on-primary shadow-sm hover:bg-primary/85`;
const PILL_GLASS = `${PILL} bg-surface-container-lowest/70 text-on-surface shadow-md backdrop-blur-md hover:bg-surface-container-lowest`;

const ANCHORS = [
  { href: '#services', labelKey: 'landing.services' },
  { href: '#association', labelKey: 'landing.about' },
];

/**
 * The public page. Its words are the client's ("Landing Page Explained.docx",
 * 2026-10-13) and are pinned by `__tests__/brief.spec.ts`; its composition is
 * DESIGN.md's. What is deliberately absent: student and anniversary counters
 * and an "about" paragraph (nobody gave us either), a menu button (one
 * scrolling page), links on the six panels (a signed-out visitor would meet a
 * login wall six times).
 */
export function LandingPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-surface text-on-surface">
      {/* One floating glass pill, as on the source site. */}
      <header className="fixed inset-x-5 top-5 z-40 mx-auto flex h-14 max-w-5xl items-center justify-between gap-2 rounded-full border border-hairline bg-surface/80 pl-3 pr-1.5 shadow-sm backdrop-blur-xl sm:inset-x-10 sm:pl-4">
        <a href="#top" className="flex min-h-[44px] shrink-0 items-center gap-2">
          <img
            src={HELIOS_LOGO.src}
            srcSet={HELIOS_LOGO.srcSet}
            width={32}
            height={32}
            alt=""
            className="h-8 w-8 rounded-lg"
          />
          {/* Under 390px the French "Se connecter" needs the room; the h1 names the product. */}
          <span className="hidden font-display text-[1.25rem] leading-none tracking-[-0.03em] min-[390px]:inline">
            MySCAW
          </span>
        </a>
        <nav aria-label={t('landing.services')} className="hidden items-center gap-1 md:flex">
          {ANCHORS.map((anchor) => (
            <a
              key={anchor.href}
              href={anchor.href}
              className="inline-flex min-h-[44px] items-center rounded-full px-4 text-body-sm text-on-surface-variant transition-colors duration-chip hover:text-on-surface"
            >
              {t(anchor.labelKey)}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-1.5 sm:gap-3">
          <LanguageSwitcher />
          <Link
            to={user ? '/dashboard' : '/login'}
            className="inline-flex min-h-[44px] items-center justify-center whitespace-nowrap rounded-full bg-primary px-3.5 text-body-sm font-medium text-on-primary sm:px-4 transition-colors duration-chip hover:bg-primary/85"
          >
            {user ? t('landing.openApp') : t('landing.signIn')}
          </Link>
        </div>
      </header>

      <main id="top" className="flex flex-col gap-16 px-3 pt-3 sm:px-5 lg:gap-24">
        {/*
          The first panel of the source site: a sky, the headline and its
          actions centred, the picture rising out of a paper collage. The
          floating header rides over the top of it, as it does there. It runs
          nearly edge to edge on a desktop while the sections under it keep the
          reading width.
        */}
        <section
          aria-labelledby="landing-hero"
          className="relative isolate mx-auto flex w-full max-w-[112rem] flex-col items-center gap-5 overflow-hidden rounded-3xl bg-gradient-to-b from-panel-sky to-panel-sky-pale px-5 pb-10 pt-24 text-center sm:px-10 sm:pb-10 sm:pt-28 lg:rounded-[40px] lg:pt-32"
        >
          <HeroCollage />
          <h1 id="landing-hero" className="font-display text-display">
            {t('landing.heroTitle1')} <span className="italic">{t('landing.heroTitle2')}</span>
          </h1>
          <p className="max-w-[44ch] text-body-lg text-on-surface/80">
            {t('landing.heroParagraph')}
          </p>
          <div className="mt-2 flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            {user ? (
              <Link to="/dashboard" className={PILL_INK}>
                {t('landing.openApp')}
              </Link>
            ) : (
              <>
                <Link to="/signup" className={PILL_INK}>
                  {t('landing.join')}
                </Link>
                <Link to="/login" className={PILL_GLASS}>
                  {t('landing.signIn')}
                </Link>
              </>
            )}
          </div>
          <PhotoLightbox
            src={HERO_IMAGE.src}
            fullSrc={HERO_IMAGE.full}
            alt={t('landing.heroImageLabel')}
            className="relative mt-6 aspect-[3/2] w-full max-w-2xl overflow-hidden lg:max-w-3xl rounded-3xl bg-panel-sky-pale shadow-card sm:mt-10"
            imgClassName="h-full w-full object-cover"
            // React 18 only passes the lowercase attribute through; the camelCase
            // prop arrives with React 19.
            imgProps={{
              ...{ fetchpriority: 'high' },
              srcSet: HERO_IMAGE.srcSet,
              sizes: HERO_IMAGE.sizes,
              width: HERO_IMAGE.width,
              height: HERO_IMAGE.height,
              decoding: 'async',
            }}
          />
        </section>

        <section
          id="services"
          aria-labelledby="landing-services"
          className="mx-auto w-full max-w-6xl scroll-mt-24 sm:px-1"
        >
          <h2
            id="landing-services"
            className="max-w-[22ch] px-2 font-headline-xl text-headline-xl sm:px-0"
          >
            {t('landing.servicesTitle')}
          </h2>
          <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:mt-12 lg:grid-cols-3 lg:gap-6">
            {SERVICES.map((service, index) => (
              <li key={service.module} className="flex">
                <Reveal
                  delay={(index % 3) * 70}
                  className={`flex w-full flex-col gap-10 rounded-3xl p-6 ${CHIP[service.module]}`}
                >
                  <span
                    aria-hidden
                    className="material-symbols-outlined flex h-12 w-12 items-center justify-center rounded-full bg-surface-container-lowest/70 text-[24px]"
                  >
                    {service.icon}
                  </span>
                  <div className="flex flex-col gap-2">
                    <h3 className="font-display text-headline-lg font-normal text-on-surface">
                      {t(service.titleKey)}
                    </h3>
                    <p className="text-body-md">{t(service.descriptionKey)}</p>
                  </div>
                </Reveal>
              </li>
            ))}
          </ul>
        </section>

        <section
          id="association"
          aria-labelledby="landing-association"
          className="mx-auto w-full max-w-6xl scroll-mt-24 sm:px-1"
        >
          <Reveal className="on-ink grid gap-8 rounded-3xl bg-association px-5 py-10 text-on-association sm:p-10 lg:grid-cols-12 lg:items-center lg:gap-x-12 lg:rounded-[40px] lg:p-14">
            <div className="flex flex-col gap-4 lg:col-span-5">
              <img
                src={HELIOS_LOGO.src}
                srcSet={HELIOS_LOGO.srcSet}
                width={96}
                height={96}
                alt={t('landing.helioLogoAlt')}
                loading="lazy"
                className="h-20 w-20 rounded-2xl"
              />
              <h2 id="landing-association" className="font-headline-xl text-headline-xl">
                {t('landing.associationName')}
              </h2>
              <p className="font-display text-headline-lg font-normal italic text-association-sun">
                {t('landing.associationTagline')}
              </p>
            </div>
            <div className="relative aspect-[4/3] overflow-hidden rounded-3xl bg-on-association/10 lg:col-span-7 lg:row-span-2">
              <AboutSlideshow />
            </div>
            <ul className="flex flex-col gap-2 lg:col-span-5">
              {SOCIALS.map((social) => (
                <li key={social.id}>
                  <a
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-h-[52px] items-center justify-between gap-4 rounded-full bg-on-association/10 px-5 transition-colors duration-chip hover:bg-on-association/20"
                  >
                    <span className="text-body-sm text-on-association/70">
                      {t(social.labelKey)}
                    </span>
                    <span className="flex items-center gap-2 text-body-md font-medium">
                      {t(social.handleKey)}
                      <span aria-hidden className="material-symbols-outlined text-[18px]">
                        north_east
                      </span>
                    </span>
                    <span className="sr-only"> ({t('landing.opensInNewTab')})</span>
                  </a>
                </li>
              ))}
            </ul>
          </Reveal>
        </section>
      </main>

      <footer className="mx-auto flex max-w-6xl flex-col items-center gap-1 px-5 pb-28 pt-12 text-center sm:pb-12">
        <nav aria-label={t('legal.links.terms')} className="flex flex-wrap justify-center gap-x-6">
          <Link
            to="/privacy"
            className="inline-flex min-h-[44px] items-center text-body-sm text-on-surface-variant transition-colors duration-chip hover:text-on-surface"
          >
            {t('legal.links.privacy')}
          </Link>
          <Link
            to="/terms"
            className="inline-flex min-h-[44px] items-center text-body-sm text-on-surface-variant transition-colors duration-chip hover:text-on-surface"
          >
            {t('legal.links.terms')}
          </Link>
        </nav>
        <p className="text-body-sm text-on-surface-variant">{t('landing.footer')}</p>
      </footer>
    </div>
  );
}
